








































import { mixins } from "vue-class-component";
import { Component, Prop } from "vue-property-decorator";
import { PaymentSystemPublic } from "@scrile/api-provider/dist/api/PaymentSystemsProvider";
import { RecurringBillingScheduleSubjectTypeEnum } from "@scrile/api-provider/dist/api/RecurringBillingScheduleProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import WithAuthUser from "@/mixins/WithAuthUser";
import providers from "@/lib/api";
import { AuthorisedOnly } from "@/lib/decorators";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { formatMoneyString, getMainCurrencyCode, getCostByCurrency } from "@/lib/moneyHelpers";
import PaymentManager from "@/payments";
import { EVENTS } from "@/const";
import BaseSpinner from "@/components/BaseSpinner";
import ModalView from "@/components/ModalView";
import SubscriptionConfirmModal from "./components/SubscriptionConfirmModal.vue";
import SubscriptionItem from "./components/SubscriptionItem.vue";

@Component({
  name: "UserSubscribeModal",
  components: { SubscriptionItem, ModalView, BaseSpinner, SubscriptionConfirmModal },
})
export default class UserSubscribeModal extends mixins(WithAuthUser) {
  @Prop({ type: Object, required: true })
  public readonly user!: UserPublicData;

  @Prop({ type: String, default: $t("Choose subscription") })
  public readonly title!: string;

  @Prop({ type: String })
  public readonly subscriptionLevelId!: string | undefined;

  @Prop({ type: Boolean, required: true })
  public readonly show!: boolean;

  public processingLevelId: string = "";

  public loading: boolean = false;

  public showSubscriptionConfirmation: boolean = false;

  public selectedSubscriptionLevel: SubscriptionLevel | null = null;

  public subscriptionLevels: SubscriptionLevel[] = [];

  public maxCurrentSubscription: SubscriptionLevel | null = null;

  public publicPaymentSystems: PaymentSystemPublic[] | null = null;

  public get filteredSubscriptionLevels(): SubscriptionLevel[] {
    if (!this.subscriptionLevelId) {
      return this.subscriptionLevels.filter(level => !!level.contentCount && !level.disabled);
    }
    const amount =
      this.subscriptionLevels.find(level => level.id === this.subscriptionLevelId)?.pricePackage?.amount ?? 0;
    return this.subscriptionLevels.filter(
      level => (level?.pricePackage?.amount ?? 0) >= amount && !!level.contentCount && !level.disabled
    );
  }

  get mainCurrency() {
    return getMainCurrencyCode();
  }

  get paymentSystemId() {
    return this.publicPaymentSystems?.find(publicSystem => publicSystem.currencies.includes(this.mainCurrency))?.id;
  }

  get selectedFeedSubscriptionLevelId() {
    if (this.subscriptionLevelId) {
      return this.subscriptionLevelId;
    }
    if (this.subscriptionLevels.length > 0) {
      return this.subscriptionLevels[0].id;
    }
    return "";
  }

  get cost() {
    return this.selectedSubscriptionLevel?.pricePackage
      ? getCostByCurrency(this.selectedSubscriptionLevel?.pricePackage, this.mainCurrency)?.cost
      : 0;
  }

  @AuthorisedOnly(ctx => ctx.user.id)
  public handleSubscribe(selectedSubscriptionLevel: SubscriptionLevel) {
    if (this.showSubscriptionConfirmation) return;
    this.showSubscriptionConfirmation = true;
    this.selectedSubscriptionLevel = selectedSubscriptionLevel;
  }

  public cancelUpgradeLevel() {
    this.selectedSubscriptionLevel = null;
    this.showSubscriptionConfirmation = false;
  }

  public async handleConfirmSubscribe() {
    if (!this.paymentSystemId || this.processingLevelId || !this.selectedSubscriptionLevel) return;
    try {
      this.showSubscriptionConfirmation = false;
      this.processingLevelId = this.selectedSubscriptionLevel.id;
      const price = (this.cost && formatMoneyString(this.cost, { currencyCode: this.mainCurrency })) || undefined;
      await PaymentManager.subscribe(
        {
          subjectType: RecurringBillingScheduleSubjectTypeEnum.SUBSCRIPTION,
          subjectId: this.selectedSubscriptionLevel.id,
        },
        this.selectedSubscriptionLevel.pricePackageId,
        price
      );

      emitter.emit(EVENTS.CONTENT_FEED_PURCHASED, { userId: this.user.id });
      emitter.emit(EVENTS.SUBSCRIPTION_STATUS_CHANGED, { userId: this.user.id, followingStatus: true });

      this.$emit("close", true);
    } finally {
      this.processingLevelId = "";
      this.selectedSubscriptionLevel = null;
    }
  }

  public async getMaxCurrentSubscriptionId() {
    let maxSubscriptionId: string | null = null;
    if (this.authUser) {
      const subscriptions = await providers.SubscriptionProvider.search({
        data: {
          providerId: this.user.id,
          consumerIds: [this.authUser.id],
          expired: false,
        },
      });
      maxSubscriptionId =
        subscriptions.result.length > 0
          ? subscriptions.result.reduce((acc, curr) => (acc.amount > curr.amount ? acc : curr)).levelId
          : null;
    }
    return maxSubscriptionId;
  }

  public async created() {
    try {
      this.loading = true;
      await providers.PaymentSystemsProvider.findAllPublic().then(r => (this.publicPaymentSystems = r));
      const maxSubscriptionId = await this.getMaxCurrentSubscriptionId();
      await providers.SubscriptionLevelProvider.find({ userIds: [this.user.id] }, [
        "id",
        "userId",
        "pricePackageId",
        "description",
        { pricePackage: ["amount", { costs: ["cost", "currency"] }] },
        "contentCount",
        "disabled",
      ]).then(r => {
        this.$emit("levelsLoaded", r);
        this.maxCurrentSubscription = r.find(subscription => subscription.id === maxSubscriptionId) ?? null;
        this.subscriptionLevels = r;
      });
    } finally {
      this.loading = false;
    }
  }
}
